/*
These are equivalent to window.alert and window.confirm, intended for simple use.
They are not intended to be used for complex dialogs, or for dialogs that require
user input, custom controls, etc. For those, use the Dialog component directly.

Please don't overuse.

*/

import * as React from 'react';
import * as Dialog from '@naan/primitives/modals/dialog';
import { AppFactory } from '@app/app-factory';

import __ from 'core/lib/localization';

const SimpleConfirm = ({
  body,
  onOk,
  onCancel = () => {},
}: {
  body: React.ReactNode;
  onOk: () => void;
  onCancel?: () => void;
}) => {
  return (
    <Dialog.Container open onDismiss={onCancel}>
      <Dialog.Body>{body}</Dialog.Body>
      <Dialog.ButtonsContainer>
        <Dialog.Button
          label={__('Cancel', 'common.cancel')}
          onClick={onCancel}
          presentation={'grayTransparent'}
        />
        <Dialog.Button
          label={__('Ok', 'common.ok')}
          onClick={onOk}
          presentation={'teal'}
        />
      </Dialog.ButtonsContainer>
    </Dialog.Container>
  );
};

type SimpleConfirmProps = React.ComponentProps<typeof SimpleConfirm>;

export const presentSimpleConfirmation = (
  body: SimpleConfirmProps['body'],
  onOk: SimpleConfirmProps['onOk'],
  onCancel: SimpleConfirmProps['onCancel'] = () => {}
) => {
  AppFactory.dialogPresenter.present(onDismiss => (
    <SimpleConfirm
      body={body}
      onOk={() => {
        onDismiss();
        onOk();
      }}
      onCancel={() => {
        onDismiss();
        onCancel?.();
      }}
    />
  ));
};

const SimpleAlert = ({
  body,
  onDismiss = () => {},
}: {
  body: React.ReactNode;
  onDismiss?: () => void;
}) => {
  return (
    <Dialog.Container open onDismiss={onDismiss}>
      <Dialog.Body>{body}</Dialog.Body>
      <Dialog.ButtonsContainer>
        <Dialog.Button
          label={__('Ok', 'common.ok')}
          onClick={onDismiss}
          presentation={'teal'}
        />
      </Dialog.ButtonsContainer>
    </Dialog.Container>
  );
};

export const presentSimpleAlert = (
  body: React.ComponentProps<typeof SimpleAlert>['body'],
  onOk?: () => void
) => {
  AppFactory.dialogPresenter.present(onDismiss => (
    <SimpleAlert
      body={body}
      onDismiss={() => {
        onDismiss();
        onOk?.();
      }}
    />
  ));
};
